import { RefObject, useEffect } from 'react';
import { PostImpressionSource, usersApi } from 'apis/CompanyAPI/users/usersApi';
import { CompanyImpressionSource, companyUpdateAPI } from 'apis/CompanyAPI/content/UpdateAPI';
import { createImpressionQueue } from 'domain/Updates/shared/impressionQueue';
import useIsElementVisible from './useIsElementVisible';

const FLUSH_INTERVAL_MS = 5000;
// A post counts as seen when at least half of it has been visible for a full second
const VISIBLE_THRESHOLD = 0.5;
const VISIBLE_FOR_MS = 1000;

/** Where the post was on screen: a list of posts (the feed, a community, room or boardroom), or a company's updates tab or company card */
export type ImpressionTarget =
  | { type: 'list'; source: PostImpressionSource }
  | { type: 'company'; companyId: number; source: CompanyImpressionSource };

const targetKey = (target: ImpressionTarget) =>
  target.type === 'list' ? `list:${target.source}` : `company:${target.companyId}:${target.source}`;

// One queue per target, since each target is a different endpoint or source
const queues = new Map<string, ReturnType<typeof createImpressionQueue>>();
let flushTimer: ReturnType<typeof setTimeout> | undefined;
let listeningForPageHide = false;

function queueFor(target: ImpressionTarget) {
  const key = targetKey(target);
  let queue = queues.get(key);
  if (!queue) {
    queue = createImpressionQueue(ids =>
      target.type === 'list'
        ? usersApi.logImpressions(ids, target.source)
        : companyUpdateAPI(target.companyId).logImpressions(ids, target.source),
    );
    queues.set(key, queue);
  }
  return queue;
}

function flush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = undefined;
  queues.forEach(queue => queue.flush());
}

function enqueue(contentId: string, target: ImpressionTarget) {
  const queue = queueFor(target);
  queue.add(contentId);
  if (!listeningForPageHide) {
    listeningForPageHide = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
  }
  if (queue.hasPending() && !flushTimer) {
    flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
  }
}

/**
 * Logs an impression for `contentId` once the element in `ref` has been at least 50% visible for 1 second.
 * Pass an undefined `contentId` to skip logging, e.g. for the company's own admins.
 */
export default function usePostImpression(
  contentId: string | undefined,
  ref: RefObject<HTMLElement>,
  target: ImpressionTarget,
) {
  const isVisible = useIsElementVisible(ref, VISIBLE_THRESHOLD, VISIBLE_FOR_MS);
  const key = targetKey(target);

  useEffect(() => {
    if (isVisible && contentId) enqueue(contentId, target);
  }, [isVisible, contentId, key]);
}
